import { initializeApp } from 'firebase/app';
import {
	getAnalytics,
	logEvent,
	Analytics,
	setAnalyticsCollectionEnabled,
} from 'firebase/analytics';

// 🔹 Firebase Configuration
const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
	measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// 🔹 Initialize Firebase (Ensuring it's only initialized in the client-side)
export const firebaseApp = initializeApp(firebaseConfig);
export const analytics: Analytics | null =
	typeof window !== 'undefined' ? getAnalytics(firebaseApp) : null;

// 🔹 Enable Debug Mode Globally (For Firebase DebugView)
if (analytics) {
	setAnalyticsCollectionEnabled(analytics, true);

	// Enable debug mode globally in GA4
	window.gtag?.('set', 'debug_mode', true);
}

/**
 * Logs a Firebase Analytics event (Ensures debug mode is enabled for all events).
 * @param eventName - The name of the event (e.g., "login", "page_view").
 * @param eventParams - Optional parameters to send with the event.
 */
export const logFirebaseEvent = (
	eventName: string,
	eventParams: Record<string, any> = {},
): void => {
	if (typeof window !== 'undefined' && analytics) {
		// Ensure debug_mode is always true for DebugView tracking
		const eventParamsWithDebug = { ...eventParams, debug_mode: true };
		logEvent(analytics, eventName, eventParamsWithDebug);
	} else {
		console.warn(`Firebase Analytics event "${eventName}" not logged (Analytics not available).`);
	}
};

/* ============================
   🔹 AUTH & USER EVENTS 
   ============================ */

export const logEmailLogin = (): void => logFirebaseEvent('login', { method: 'email_password' });
export const logEmailSignUp = (): void => logFirebaseEvent('sign_up', { method: 'email_password' });
export const logSocialLogin = (provider: string): void =>
	logFirebaseEvent('login', { method: provider });
export const logSocialSignUp = (provider: string): void =>
	logFirebaseEvent('sign_up', { method: provider });
export const logLogout = (): void => logFirebaseEvent('logout');

/* ============================
   🔹 JOB PORTAL EVENTS 
   ============================ */

export const logJobView = (jobId: string): void => logFirebaseEvent('job_view', { job_id: jobId });
export const logJobApply = (jobId: string): void =>
	logFirebaseEvent('job_apply', { job_id: jobId });
export const logJobSaved = (jobId: string): void =>
	logFirebaseEvent('job_saved', { job_id: jobId });

/* ============================
   🔹 SOCIAL FEEDS & STORIES 
   ============================ */

export const logPostCreated = (postType: string): void =>
	logFirebaseEvent('post_created', { post_type: postType });
export const logPostLiked = (postId: string): void =>
	logFirebaseEvent('post_liked', { post_id: postId });
export const logStoryViewed = (storyId: string): void =>
	logFirebaseEvent('story_viewed', { story_id: storyId });

/* ============================
   🔹 CHAT EVENTS 
   ============================ */

export const logMessageSent = (chatId: string): void =>
	logFirebaseEvent('message_sent', { chat_id: chatId });
export const logMessageReceived = (chatId: string): void =>
	logFirebaseEvent('message_received', { chat_id: chatId });

/* ============================
   🔹 PACKAGE & PURCHASE EVENTS 
   ============================ */

export const logPurchaseStarted = (packageId: string): void =>
	logFirebaseEvent('purchase_started', { package_id: packageId });
export const logPurchaseCompleted = (packageId: string): void =>
	logFirebaseEvent('purchase_completed', { package_id: packageId });

/* ============================
   🔹 STATIC PAGES & LOGO EVENTS 
   ============================ */

export const logPageView = (pageName: string): void =>
	logFirebaseEvent('page_view', { page: pageName });
export const logLogoClick = (location: string): void =>
	logFirebaseEvent('logo_clicked', { location });

/* ============================
   🔹 BLOG EVENTS 
   ============================ */

export const logBlogView = (blogId: string): void =>
	logFirebaseEvent('blog_view', { blog_id: blogId });
